<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const API_URL = import.meta.env.VITE_API_URL || ''
const showBanner = ref(false)
const showSettings = ref(false)
const cookiePolicyTitle = ref('Cookie Policy')

const cookiePrefs = ref({
  essential: true,
  analytics: true,
  marketing: false,
  functional: true
})

onMounted(async () => {
  const consent = localStorage.getItem('cookieConsent')
  if (!consent) {
    showBanner.value = true
  }
  try {
    const { data } = await axios.get(`${API_URL}/api/pages/cookies`)
    if (typeof data?.title === 'string' && data.title.trim()) {
      cookiePolicyTitle.value = data.title.trim()
    }
  } catch {}
})

const saveConsent = (prefs) => {
  localStorage.setItem('cookieConsent', JSON.stringify(prefs))
  showBanner.value = false
}

const acceptAll = () => {
  saveConsent({ essential: true, analytics: true, marketing: true, functional: true })
}

const rejectAll = () => {
  saveConsent({ essential: true, analytics: false, marketing: false, functional: false })
}

const savePreferences = () => {
  saveConsent({ ...cookiePrefs.value, essential: true })
}

const toggleSettings = () => {
  showSettings.value = !showSettings.value
}

const openCookieNotice = () => {
  router.push({ name: 'Cookies' })
}
</script>

<template>
  <Transition name="cookie-slide">
    <div v-if="showBanner" class="cookie-overlay">
      <div class="cookie-popup">
        <div class="cookie-header">
          <h2 class="cookie-title">Cookies usage</h2>
          <button class="cookie-close" @click="rejectAll" title="Close">×</button>
        </div>
        <p class="cookie-message">
          This Website uses cookies to ensure that it functions properly and to improve your experience. Essential cookies are used automatically for critical operations, while optional cookies are available to enhance your browsing experience. You have the choice to adjust settings for these optional cookies as you see fit. To understand more about how and why we use cookies, please check out our detailed <a class="cookie-link" @click.prevent="openCookieNotice">{{ cookiePolicyTitle }}</a>.
        </p>

        <Transition name="settings-expand">
          <div v-if="showSettings" class="cookie-settings">
            <div class="cookie-category">
              <div class="category-info">
                <span class="category-name">Essential Cookies</span>
                <span class="category-desc">Required for the website to function. Cannot be disabled.</span>
              </div>
              <label class="toggle always-on">
                <input type="checkbox" :checked="true" disabled />
                <span class="slider"></span>
              </label>
            </div>

            <div class="cookie-category">
              <div class="category-info">
                <span class="category-name">Analytics Cookies</span>
                <span class="category-desc">Help us understand how visitors interact with our website.</span>
              </div>
              <label class="toggle">
                <input type="checkbox" v-model="cookiePrefs.analytics" />
                <span class="slider"></span>
              </label>
            </div>

            <div class="cookie-category">
              <div class="category-info">
                <span class="category-name">Marketing Cookies</span>
                <span class="category-desc">Used to deliver personalized advertisements.</span>
              </div>
              <label class="toggle">
                <input type="checkbox" v-model="cookiePrefs.marketing" />
                <span class="slider"></span>
              </label>
            </div>

            <div class="cookie-category">
              <div class="category-info">
                <span class="category-name">Functional Cookies</span>
                <span class="category-desc">Enable enhanced functionality and personalization.</span>
              </div>
              <label class="toggle">
                <input type="checkbox" v-model="cookiePrefs.functional" />
                <span class="slider"></span>
              </label>
            </div>
          </div>
        </Transition>

        <div class="cookie-actions">
          <button class="cookie-btn btn-settings" @click="toggleSettings">
            {{ showSettings ? 'Hide' : 'Settings' }}
          </button>
          <button v-if="showSettings" class="cookie-btn btn-save" @click="savePreferences">Save</button>
          <button class="cookie-btn btn-reject" @click="rejectAll">Reject</button>
          <button class="cookie-btn btn-accept" @click="acceptAll">Accept All</button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.cookie-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  justify-content: center;
  padding: 2rem;
  pointer-events: none;
}

.cookie-popup {
  pointer-events: all;
  background: rgba(6, 10, 16, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  color: #fff;
  border: 1px solid rgba(0, 212, 255, 0.15);
  border-radius: 16px;
  padding: 2rem 2.5rem;
  max-width: 680px;
  width: 100%;
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.5), 0 0 80px rgba(0, 212, 255, 0.06);
}

.cookie-header {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: 1rem;
}

.cookie-title {
  font-size: 1.25rem;
  font-weight: 800;
  margin: 0;
  color: #fff;
  text-transform: uppercase;
  text-align: center;
}

.cookie-close {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
}

.cookie-close:hover {
  color: #fff;
}

.cookie-message {
  font-size: 0.88rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.75);
  margin: 0 0 1.5rem 0;
}

.cookie-link {
  color: #00d4ff;
  text-decoration: none;
  cursor: pointer;
  font-weight: 600;
  transition: color 0.2s ease;
}

.cookie-link:hover {
  color: #33ddff;
  text-decoration: underline;
}

/* Cookie Settings Panel */
.cookie-settings {
  border-top: 1px solid rgba(0, 212, 255, 0.12);
  padding-top: 1.25rem;
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cookie-category {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: rgba(0, 212, 255, 0.04);
  border-radius: 10px;
  border: 1px solid rgba(0, 212, 255, 0.08);
}

.category-info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
  margin-right: 1rem;
}

.category-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
}

.category-desc {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.4;
}

/* Toggle Switch */
.toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  min-width: 44px;
  height: 24px;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.15);
  transition: 0.3s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: rgba(255, 255, 255, 0.5);
  transition: 0.3s;
  border-radius: 50%;
}

.toggle input:checked + .slider {
  background-color: #00d4ff;
  box-shadow: 0 0 12px rgba(0, 212, 255, 0.35);
}

.toggle input:checked + .slider:before {
  transform: translateX(20px);
  background-color: #060a10;
}

.toggle.always-on .slider {
  background-color: #00d4ff;
  opacity: 0.55;
  cursor: not-allowed;
}

.toggle.always-on .slider:before {
  transform: translateX(20px);
  background-color: #060a10;
}

/* Actions */
.cookie-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.cookie-btn {
  padding: 0.6rem 1.5rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.btn-settings {
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.btn-settings:hover {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.35);
}

.btn-save {
  background: transparent;
  color: #00d4ff;
  border: 1px solid rgba(0, 212, 255, 0.4);
}

.btn-save:hover {
  background: rgba(0, 212, 255, 0.1);
  border-color: #00d4ff;
}

.btn-reject {
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-reject:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.btn-accept {
  background: transparent;
  color: #fff;
  border: 1px solid rgba(0, 212, 255, 0.4);
}

.btn-accept::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.18), transparent);
  transition: left 0.5s ease;
}

.btn-accept:hover::before {
  left: 100%;
}

.btn-accept:hover {
  background: #00d4ff;
  color: #060a10;
  border-color: #00d4ff;
  transform: translateY(-2px);
  box-shadow: 0 5px 20px rgba(0, 212, 255, 0.3);
}

/* Settings expand transition */
.settings-expand-enter-active {
  transition: all 0.3s ease-out;
}

.settings-expand-leave-active {
  transition: all 0.2s ease-in;
}

.settings-expand-enter-from,
.settings-expand-leave-to {
  opacity: 0;
  max-height: 0;
  margin-bottom: 0;
  padding-top: 0;
}

/* Slide transition */
.cookie-slide-enter-active {
  transition: all 0.4s ease-out;
}

.cookie-slide-leave-active {
  transition: all 0.3s ease-in;
}

.cookie-slide-enter-from,
.cookie-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@media (max-width: 768px) {
  .cookie-overlay {
    padding: 1rem;
  }

  .cookie-popup {
    padding: 1.5rem;
    max-height: 85vh;
    overflow-y: auto;
  }

  .cookie-title {
    font-size: 1.1rem;
  }

  .cookie-message {
    font-size: 0.82rem;
  }

  .cookie-actions {
    gap: 0.5rem;
  }

  .cookie-btn {
    padding: 0.55rem 1rem;
    font-size: 0.8rem;
  }

  .cookie-category {
    padding: 0.6rem 0.75rem;
  }

  .category-name {
    font-size: 0.83rem;
  }

  .category-desc {
    font-size: 0.73rem;
  }
}
</style>
